<template>
  <div>
    <b-overlay
      :show="show"
      :variant="variant"
      :opacity="opacity"
      :blur="blur"
      rounded="lg"
      spinner-variant="primary"
    >
    <b-card v-if="recordNotFound">
      <b>{{$t("NoRecordFound")}}</b>
      <router-link :to="'/company/staff'" class="float-right">
          <b-button variant="primary" class="primary">{{
            $t("Back")
          }}</b-button>
        </router-link>
    </b-card>
      <b-card v-else>
        <h1 v-if="this.$route.params.id">{{ $t("EditStaff") }}</h1>
        <h1 v-else>{{ $t("AddStaff") }}</h1>
        <form
          name="staff_form"
          method="post"
          v-on:submit.prevent="$event.preventDefault()"
        >
          <div class="vx-row">
            <!-- required -->
            <div
              class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
            >
              <label class="vs-input--label"
                >{{ $t("FirstName") }} <span class="red">*</span></label
              >
              <b-input
                size="lg"
                v-validate="'required'"
                :data-vv-as="$t('FirstName')"
                name="first_name"
                v-model="staffs.first_name"
                class="w-full"
                @keydown.enter="checkForm"
                :placeholder="$t('FirstName')"
                :label="$t('FirstName')"
              />
              <span
                class="text-danger text-sm"
                v-show="errors.has('first_name')"
                >{{ errors.first("first_name") }}</span
              >
            </div>
            <div
              class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
            >
              <label class="vs-input--label"
                >{{ $t("LastName") }} <span class="red">*</span></label
              >
              <b-input
                size="lg"
                v-validate="'required'"
                name="last_name"
                :data-vv-as="$t('LastName')"
                v-model="staffs.last_name"
                class="w-full"
                @keydown.enter="checkForm"
                :placeholder="$t('LastName')"
              />
              <span
                class="text-danger text-sm"
                v-show="errors.has('last_name')"
                >{{ errors.first("last_name") }}</span
              >
            </div>

            <div
              class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
              id="department"
            >
              <label class="vs-input--label">{{ $t("Departments") }}</label>
              <v-select
                multiple
                size="lg"
                v-model="staffs.department_id"
                :options="options"
                :reduce="(department_name) => department_name.id"
                label="department_name"
                :placeholder="$t('SelectDepartments')"
              >
                <template v-slot:option="option">
                  <span :class="option.icon"
                    >{{ option.department_name }}
                  </span>
                  <feather-icon
                    v-if="
                      staffs.department_id &&
                      staffs.department_id.includes(option.id)
                    "
                    icon="CheckIcon"
                    class="float-right"
                    svgClasses="w-5 h-5"
                  />
                </template>
              </v-select>
              <span id="department_id" class="text-danger text-sm">{{
                check
              }}</span>
            </div>

            <!-- Must only consist of numbers -->
            <div
              class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-4"
              id="contact_label"
            >
              <label for class="vs-input--label">{{
                $t("ContactNumber")
              }}</label>
              <vue-tel-input
                size="lg"
                v-validate="{
                  required: true,
                  regex: /^(?=.*[0-9])[- +()0-9]+$/,
                  min: 12,
                }"
                v-model="staffs.contact_number"
                class="mt-1 w-full contact_number"
                :data-vv-as="$t('ContactNumber')"
                v-on:country-changed="countryChanged"
                v-bind="bindProps"
                @onInput="onInput"
                @keydown.enter="checkForm"
              ></vue-tel-input>

              <span
                class="text-danger text-sm"
                v-show="errors.has('contact_number')"
                >{{ errors.first("contact_number") }}</span
              >
            </div>

            <!-- Must be a valid email -->
            <div
              class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
            >
              <label class="vs-input--label"
                >{{ $t("Email") }} <span class="red">*</span></label
              >
              <b-input
                size="lg"
                autocomplete="off"
                v-validate="'required|email'"
                name="email"
                :data-vv-as="$t('Email')"
                v-model="staffs.email"
                class="w-full"
                @keydown.enter="checkForm"
                :placeholder="$t('Email')"
              />
              <span class="text-danger text-sm" v-show="errors.has('email')">{{
                errors.first("email")
              }}</span>
            </div>
            <div
              class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
            >
              <label class="vs-input--label"
                >{{ $t("Password") }} <span class="red">*</span></label
              >
              <b-input
                type="password"
                autocomplete="new-password"
                v-if="$route.params.id"
                size="lg"
                v-validate="'min:8|numbers|upCase|loCase'"
                :data-vv-as="$t('Password')"
                name="password"
                v-model="staffs.password"
                class="w-full"
                @keydown.enter="checkForm"
                @input="displayError"
                :placeholder="$t('Password')"
              />
              <b-input
                type="password"
                autocomplete="new-password"
                v-else
                size="lg"
                v-validate="'required|min:8|numbers|upCase|loCase'"
                :data-vv-as="$t('Password')"
                name="password"
                v-model="staffs.password"
                class="w-full"
                @keydown.enter="checkForm"
                @input="displayError"
                :placeholder="$t('Password')"
              />

              <span
                class="text-danger text-sm"
                v-show="errors.has('password')"
                >{{ errors.first("password") }}</span
              >
            </div>

            <div
              class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
            >
              <label class="vs-input--label"
                >{{ $t("Language") }} <span class="red">*</span></label
              >
              <v-select
                v-validate="'required'"
                v-model="staffs.language"
                :options="languages"
                :reduce="(label) => label.value"
                label="label"
                :placeholder="$t('SelectLanguage')"
                :data-vv-as="$t('Language')"
                name="language"
              >
                <template v-slot:option="option">
                  <span :class="option.icon">{{ option.label }} </span>
                  <feather-icon
                    v-if="staffs.language && staffs.language == option.value"
                    icon="CheckIcon"
                    class="float-right"
                    svgClasses="w-5 h-5"
                  />
                </template>
              </v-select>

              <span
                class="text-danger text-sm"
                v-show="errors.has('language')"
                >{{ errors.first("language") }}</span
              >
            </div>
            <div
              class="vx-col w-full xs:w-full sm:w-full lg:w-1/2 md:w-1/2 xl:w-1/2 mt-5"
            >
              <label class="vs-input--label"
                >{{ $t("Role") }} <span class="red">*</span></label
              >
              <v-select
                v-validate="'required'"
                v-model="selected"
                :options="roles"
                :reduce="(label) => label.value"
                label="label"
                :placeholder="$t('SelectRole')"
                :data-vv-as="$t('Role')"
                name="user_role"
                :clearable="false"
              >
                <template v-slot:option="option">
                  <span :class="option.icon">{{ option.value }} </span>
                  <feather-icon
                    v-if="selected == option.value"
                    icon="CheckIcon"
                    class="float-right"
                    svgClasses="w-5 h-5"
                  />
                </template>
              </v-select>

              <span
                class="text-danger text-sm"
                v-show="errors.has('user_role')"
                >{{ errors.first("user_role") }}</span
              >
            </div>

         </div>

          <div class="export-table mt-10" v-if="selected == 'assistant'">
          <h4 class="heading">
            <feather-icon
            id="export"
            icon="UserIcon"
            svgClasses="w-5 h-5 hover:text-danger stroke-current"
          />
          {{ $t("StaffMembers") }}
          
        </h4>
          <b-col lg="5" class="w-1/2 float-right">
            <b-form-group
              label-for="filter-input"
              label-cols-sm="3"
              label-align-sm="right"
              label-size="sm"
              class="mb-5"
            >
              <b-input-group size="md">
                <b-input-group-prepend is-text>
                  <b-icon icon="search"></b-icon>
                </b-input-group-prepend>
                <b-form-input
                  id="filter-input"
                  v-model="filter"
                  type="search"
                  :placeholder="$t('Search')"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-table
            :current-page="currentPage"
            :per-page="perPage"
            :items="staffList"
            :fields="fields"
            :busy="isBusy"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            responsive
            show-empty
            :filter="filter"
            :filter-included-fields="filterOn"
            :empty-text="$t('EmptyData')"
            :empty-filtered-text="$t('EmptyFilter')"
            @filtered="onFiltered"
          >
            <template #head()="data">
              <span>{{ $t(data.label) }}</span>
            </template>

            <template #cell(first_name)="row">
              {{row.item.first_name + " " + row.item.last_name}}
                  
            </template>
           
            <template #cell(action)="row">
              <b-form-checkbox
                  size="lg"
                  v-model="row.item.is_assistant"
                  value="1"
                  class="border-primary"
                  @change="changeAssign($event, row.item.id)"
                ></b-form-checkbox>
            </template>
            
          </b-table>

          <b-pagination
            v-if="rows > perPage"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            aria-controls="my-table"
            class="float-right"
          ></b-pagination>
           <span
                class="text-danger text-sm"
                v-show="isSubmit && getAssignendStffLength == 0"
                >{{ "Please select atleast one staff" }}</span
              >
          </div>


          <div class="vx-col" id="update">
            <b-button
              v-if="this.$route.params.id"
              variant="primary"
              @click.prevent="updateForm"
              class="mt-5 block"
              >{{ $t("Update") }}</b-button
            >
            <b-button
              v-else
              variant="primary"
              @click.prevent="submitForm"
              class="mt-5 block"
              >{{ $t("Submit") }}</b-button
            >
            <router-link :to="'/company/staff'">
              <b-button
                class="mt-5 br-20 pl-5 pr-5 border-primary text-primary"
                variant="border"
                id="cancel"
                type="border"
                >{{ $t("Cancel") }}</b-button
              >
            </router-link>
          </div>
        </form>
        <!-- popup -->

        <!--  -->
        <b-modal
          id="custom-modal"
          :title="$t('UpgradePlan')"
          v-model="newPopup"
          centered
          hide-footer
          no-close-on-backdrop
        >
          <div v-if="newPopup1">
            <div>
              <h3 class="align-center">{{ $t("SeatNotAvailable") }}</h3>
            </div>
            <div class="mt-5 export-table notes" v-if="this.$store.getters.currentUser.user_role == 'admin'">
              <b-row class="purchase">
                <b-col>
                  <b-button
                    variant="primary"
                    @click="upgradeForm"
                    id="card-button"
                    class="float-right"
                  >
                    <span>{{ $t("Upgrade") }}</span>
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </div>
          <!-- popup-2 -->
          <div v-if="newPopup2">
            <div>
              <h3 class="align-center">{{ $t("UpgradeYourPlan") }}</h3>
            </div>
            <form method="post" v-on:submit.prevent="$event.preventDefault()">
              <div class="vx-row">
                <div class="vx-col w-1/2 mt-5">
                  <label class="vs-input--label"
                    >{{ $t("Plan") }} <span class="red">*</span></label
                  >
                  <v-select
                    v-validate="'required'"
                    v-model="plan"
                    :options="planOptions"
                    :reduce="(name) => name.slug"
                    label="name"
                    :placeholder="$t('SelectPlan')"
                    :data-vv-as="$t('Plan')"
                    name="plan"
                  >
                    <template v-slot:option="option">
                      <span :class="option.icon">{{ option.slug }} </span>
                      <feather-icon
                        v-if="plan == option.slug"
                        icon="CheckIcon"
                        class="float-right"
                        svgClasses="w-5 h-5"
                      />
                    </template>
                  </v-select>
                  <span
                    class="text-danger text-sm"
                    v-show="errors.has('plan')"
                    >{{ errors.first("plan") }}</span
                  >
                </div>
                <div class="vx-col w-1/2 mt-5">
                  <label class="vs-input--label"
                    >{{ $t("Seats") }} <span class="red">*</span></label
                  >
                  <v-select
                    v-validate="'required'"
                    v-model="seat"
                    :options="seats"
                    label="name"
                    :placeholder="$t('Select Seat')"
                    :data-vv-as="$t('Seat')"
                    name="seat"
                  >
                    <template v-slot:option="option">
                      <span :class="option.icon">{{ option.name }} </span>
                      <feather-icon
                        v-if="seat == option.name"
                        icon="CheckIcon"
                        class="float-right"
                        svgClasses="w-5 h-5"
                      />
                    </template>
                  </v-select>
                  <span
                    class="text-danger text-sm"
                    v-show="errors.has('seat')"
                    >{{ errors.first("seat") }}</span
                  >
                </div>
              </div>
              <div class="mt-5 export-table notes">
                <!-- <b-row class=""> -->
                <!-- <b-col> -->
                <b-button
                  variant="primary"
                  @click="submitPlan"
                  id="card-button"
                  class="float-right"
                >
                  <span>{{ $t("Checkout") }}</span>
                </b-button>
                <!-- </b-col> -->
                <!-- </b-row> -->
              </div>
            </form>
          </div>
          <!-- popup-3 -->
          <div v-if="newPopup3" class="charge">
            <div>
              <h3 class="align-center">{{ $t("YourNewPlan") }}</h3>
            </div>
            <div class="mt-5 export-table notes ml-5 mr-5">
              <b-row>
                <b-col
                  vs-type="flex"
                  vs-justify="left"
                  vs-align="left"
                  vs-w="6"
                >
                  <td class="font-semibold">
                    {{ $t("StandardPlan") + seat + $t("SeatPlan") }}
                  </td>
                </b-col>
                <b-col
                  vs-type="flex"
                  vs-justify="right"
                  vs-align="right"
                  vs-w="6"
                >
                  <!-- <td> -->
                  <span class="float-right">&euro;{{ subtotal }}</span>
                  <br />
                  <p class="float-right">{{ $t("Billed") }}</p>
                  <!-- </td> -->
                </b-col>
              </b-row>
              <b-row class="mt-5">
                <b-col
                  vs-type="flex"
                  vs-justify="left"
                  vs-align="left"
                  vs-w="6"
                >
                  <!-- <td> -->
                  <span class="flex font-semibold"
                    >{{ $t("LeftoverBalance") }}
                    <feather-icon
                      v-b-tooltip.hover
                      :title="$t('Remain1') + remaining_days + $t('Remain2')"
                      icon="AlertCircleIcon"
                      svgClasses="h-7 w-7 stroke-current text-grey"
                      class="block ml-5"
                    />
                  </span>
                  <p>{{ $t("FromYour") }}</p>
                  <!-- </td> -->
                </b-col>
                <b-col
                  vs-type="flex"
                  vs-justify="right"
                  vs-align="right"
                  vs-w="6"
                >
                  <!-- <td> -->
                  <span class="float-right">- &euro;{{ left_balance }}</span>
                  <!-- </td> -->
                </b-col>
              </b-row>
              <hr
                class="mt-5"
                style="
                  height: 1px;
                  border-width: 0;
                  color: gray;
                  background-color: gray;
                "
              />
              <b-row class="mt-5">
                <b-col
                  vs-type="flex"
                  vs-justify="left"
                  vs-align="left"
                  vs-w="6"
                >
                  <td class="font-semibold">{{ $t("Total") }}</td>
                </b-col>
                <b-col
                  vs-type="flex"
                  vs-justify="right"
                  vs-align="right"
                  vs-w="6"
                >
                  <!-- <td> -->
                  <span class="float-right font-semibold"
                    >&euro;{{ total }}</span
                  >
                  <!-- </td> -->
                </b-col>
              </b-row>
              <b-row class="mt-5">
                <b-col
                  vs-type="flex"
                  vs-justify="left"
                  vs-align="left"
                  vs-w="6"
                >
                  <td>
                    <p style="font-size: 13px">
                      {{ $t("NextChargeDate") + expiry_date }}
                    </p>
                  </td>
                </b-col>
              </b-row>
              <b-row class="purchase">
                <b-col>
                  <b-button
                    variant="primary"
                    @click="purchasePlan"
                    id="card-button"
                    class="float-right"
                  >
                    <span>{{ $t("SubmitPurchase") }}</span>
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </div>
        </b-modal>

        <div v-if="roleVal == 'admin'">
          <credit :showCredit="showCredit" @close="close"
          @changePlan="changePlan"  @purchasePlan="purchasePlan()"></credit>
        </div>
        <!--  -->

        <!--  -->
      </b-card>
    </b-overlay>
  </div>
</template>
<script>
import vSelect from "vue-select";
import moment from "moment";
import { FormWizard, TabContent } from "vue-form-wizard";
import "vue-form-wizard/dist/vue-form-wizard.min.css";
import {
  phoneConfig,
  getLanguage,
  getRoles,
  getFilteredRoles,
  getOnUnioRoles,
} from "@/utils/helper";
import $ from "jquery";
// For custom error message
import { Validator } from "vee-validate";
import Credit from "@/components/popup/CreditStaff.vue";

export default {
  components: {
    FormWizard,
    TabContent,
    "v-select": vSelect,
    credit: Credit,
  },
  data() {
    return {
      department: this.$t("DepartmentName"),
      departments: [],
      companies: [],
      options: [],
      values: [],
      plan: "",
      planOptions: [],
      seat: "",
      seats: [1, 3, 5, 10, 15, 20, 25, 30, 40, 50, 100],
      total: "",
      subtotal: "",
      left_balance: "",
      expiry_date: "",
      remaining_days: "",
      staffs: [
        {
          first_name: "",
          last_name: "",
          department_id: "",
          company_id: "",
          email: "",
          contact_number: "",
          password: "",
          language: "",
          user_role:
            this.$store.getters.currentUser.is_unio == 1 ? "admin" : "staff",
          is_device: false,
        },
      ],
      bindProps: phoneConfig(this.$t("ContactNumber")),
      languages: getLanguage(this.$t("lang")),
      roles:
        this.$store.getters.currentUser.is_unio == 1
          ? getFilteredRoles(this.$t("roles"))
          : getOnUnioRoles(this.$t("roles")),
      selected:
        this.$store.getters.currentUser.is_unio == 1 ? "admin" : "staff",
      staff_id: "",
      newPopup: false,
      newPopup1: false,
      newPopup2: false,
      newPopup3: false,
      source: "",
      charge: [],
      show: false,
      variant: "light",
      opacity: 0.85,
      blur: "2px",
      error: "",
      success: "",
      sec: 2,
      counter: 0,
      roleVal: "",
      showCredit: false,
      staffList: [],
      perPage: 10,
      currentPage: 1,
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      filterOn: [],
      isBusy: false,
      totalRows: 1,
      checkboxValues: [],
      isSubmit: false,
      fields: [
        {
          key: "first_name",
          label: "Name",
          sortable: true,
          sortDirection: "desc",
        },
        {
          key: "department_names",
          label: "Departments",
          sortable: true,
          sortDirection: "desc",
        },
        
        { key: "action", label: "Actions" },
      ],
      recordNotFound: false
    };
  },
  watch: {
    "$i18n.locale": {
      handler: function (value) {
        this.loadLocale(value);
        this.displayError();
        this.departmentValidation();
        this.bindProps = phoneConfig(this.$t("ContactNumber"));
        this.roles =
          this.$store.getters.currentUser.is_unio == 1
            ? getFilteredRoles(this.$t("roles"))
            : getOnUnioRoles(this.$t("roles"));
        this.languages = getLanguage(this.$t("lang"));
      },
    },
  },

  created() {
    this.getStaffList();
    this.roleVal = this.$store.getters.currentUser.user_role;
    this.$validator.extend("upCase", {
      getMessage: () => this.$t("errorMessage"),
      validate: (value) => value.match(/[A-Z]/g) !== null,
    });
    this.$validator.extend("numbers", {
      getMessage: () => this.$t("errorMessage"),
      validate: (value) => value.match(/[0-9]/g) !== null,
    });
    this.$validator.extend("loCase", {
      getMessage: () => this.$t("errorMessage"),
      validate: (value) => value.match(/[a-z]/g) !== null,
    });
    this.getPlan();

    this.getDepartmentlist(this.$store.getters.roles == 'assistant' ? this.$store.getters.currentUser.company_id : this.$store.getters.currentUser.id);
    this.bindProps.disabledFetchingCountry = false;
    this.staff_id = this.$route.params.id;
    if (this.$route.params.id) {
      this.bindProps.disabledFetchingCountry = true;
      this.getstaffs(this.$route.params.id);
    }
    this.loadLocale(this.$i18n.locale);
  },
  computed: {
    rows() {
      return this.staffs.length;
    },

    check() {
      if (this.staffs.department_id != undefined) {
        return (document.getElementById("department_id").innerHTML = "");
      }
    },

    is_unio() {
      return this.$store.getters.currentUser.is_unio;
    },

    getAssignendStffLength() {
      var assignedStaff = this.checkboxValues.filter(function (val) {
        if(val.is_assign == 1)
            return val;
          });
      return assignedStaff.length;
    },
  },

  methods: {
    //display error
    displayError() {
      this.$validator.extend("upCase", {
        getMessage: () => this.$t("errorMessage"),
        validate: (value) => value.match(/[A-Z]/g) !== null,
      });
      this.$validator.extend("numbers", {
        getMessage: () => this.$t("errorMessage"),
        validate: (value) => value.match(/[0-9]/g) !== null,
      });
      this.$validator.extend("loCase", {
        getMessage: () => this.$t("errorMessage"),
        validate: (value) => value.match(/[a-z]/g) !== null,
      });
    },
    //validation error message
    errorClass(rule) {
      return {
        error: this.errors.firstByRule("password", rule),
      };
    },
    upgradeForm() {
      this.newPopup1 = false;
      this.newPopup2 = true;
    },
    submitPlan() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.show = true;
          var data = {
            company_id: this.$store.getters.currentUser.id,
            seats: this.seat,
            plan_slug: this.plan,
          };
          this.$store.dispatch("PaymentCheckout", data).then((response) => {
            this.show = false;
            this.newPopup2 = false;
            this.newPopup3 = true;
            this.total = response.data.data.total;
            this.subtotal = response.data.data.subtotal;
            this.left_balance = response.data.data.left_balance;
            this.remaining_days = response.data.data.remaining_days;
            this.expiry_date = moment(response.data.data.expiry_date).format(
              "LL"
            );
          });
        } else {
          return false;
        }
      });
    },
    /*function for get plan
      Here index is the day value
    */
    purchasePlan() {
      this.show = true;
      this.newPopup = false;
      var data = {
        company_id: this.$store.getters.currentUser.id,
        total: this.total,
        seats: this.seat,
        plan_slug: this.plan,
      };
      this.$store
        .dispatch("PurchasePlan", data)
        .then((response) => {
          console.log("response---------", response);
          this.newPopup3 = false;
          this.newPopup = false;
          this.showCredit = false;
          this.show = false;
          this.charge = response.data.data;
          if (this.charge.id) this.addstaffs();

          this.$toasted.show(this.$t("SaveTransactionPlan"), {
            className: "top-alert",
            position: "top-center",
            type: "success",
            duration: 2000,
          });
        })
        .catch((error) => {
          this.show = false;
          console.log("purchase-plan--error", error);
          // Error response
          this.error = this.$t("PurchaseFailed");
          if(this.roleVal == 'admin'){
            if(error == 'CardNotEnable'){
              this.$toasted.show(this.$t("CardNotEnabled"), {
                className: "top-alert",
                position: "top-center",
                type: "error",
                 duration: 2000,
              });
            }
            else if(error == 'CardNotAdded'){
              this.newPopup = false;
              this.newPopup3 = false;
              this.newPopup2 = false;
              this.newPopup1 = false;
              this.showCredit = true;
            }else{
              this.$toasted.show(error, {
                className: "top-alert",
                position: "top-center",
                type: "error",
                 duration: 2000,
              });
            }
          }
          else{
            this.$toasted.show(this.error, {
              className: "top-alert",
              position: "top-center",
              type: "error",
               duration: 2000,
            });
          }
          return false;
        });
    },

    getPlan() {
      //Call API for get plan
      this.$store.dispatch("GetCompanyPlan").then((response) => {
        if (response.data.seat_count) {
          this.seat = response.data.seat_count;
          let count = this.seat;

          let filterSeats = this.seats.filter(function (number) {
            return number > count;
          });
          this.seats = filterSeats;
          if (filterSeats[0]) {
            this.seat = filterSeats[0];
          }
        }
        this.planOptions = response.data.plans;
        if (this.$store.getters.currentUser.plan_name == "enterprise") {
          let oldPlan = this.$store.getters.currentUser.plan_name;
          let filterPlan = this.planOptions.filter(function (plan) {
            return oldPlan == plan.slug;
          });
          this.planOptions = filterPlan;
          this.planOptions.map((element, index) => {
            this.plan = element.slug;
          });
        } else {
          this.planOptions.map((element, index) => {
            if (element.name == "Professional") {
              this.plan = element.slug;
            }
          });
        }
      });
    },
    /**
     * validate phone number
     */
    onInput({ number, isValid, country }) {
      this.staffs.contact_number = number["international"];
    },

    /**
     * vee validate with multi lang
     */
    loadLocale(code) {
      return import("vee-validate/dist/locale/" + code).then((locale) => {
        Validator.localize(code, locale);
      });
    },

    /**
     * Country Code
     */
    countryChanged(country) {
      this.country = "+" + country.dialCode;
    },
    /**
     *  checkForm method for check form
     **/
    checkForm() {
      if (this.$route.params.id) {
        this.updateForm();
      } else {
        this.submitForm();
      }
    },
    /**
     *  checkForm method for check form
     **/
    close() {
      this.newPopup = false;
      this.newPopup1 = false;
      this.newPopup2 = false;
      this.newPopup3 = false;
      this.showCredit = false;
    },
    /**
     *  getDepartmentlist method get department data
     **/
    getDepartmentlist(id) {
      this.$store
        .dispatch("Departmentlist", id)
        .then((res) => {
          this.show = false;
          this.departments = res.data.data;
          this.options = this.departments.departments.map((department) => {
            return department;
          });
          this.values = this.departments.departments.map((department) => {
            return department.id;
          });
        })
        .catch((error) => {
          this.$log.error(
            "File : company/staff/Form.vue | Function : getDepartmentlist() | Error :",
            error
          );
          this.show = false;
          // Error response
          this.error =
            this.$t(error.replace(/[\s\/]/g, "")) !==
            error.replace(/[\s\/]/g, "")
              ? this.$t(error.replace(/[\s\/]/g, ""))
              : error;
          this.$toasted.show(this.error, {
            className: "top-alert",
            position: "top-center",
            type: "error",
            duration: 2000,
          });
          return false;
        });
    },
    /**
     * Department Validation
     */
    departmentValidation() {
      if (
        this.staffs.department_id == undefined ||
        this.staffs.department_id == null ||
        this.staffs.department_id == ""
      ) {
        document.getElementById("department_id").innerHTML = this.$t(
          "DepartmentError"
        );
      }
    },
    /**
     *  submitForm method for submiting form
     **/
    submitForm() {
      this.isSubmit = true;
      if (
        this.staffs.department_id == undefined ||
        this.staffs.department_id == null ||
        this.staffs.department_id == ""
      ) {
        document.getElementById("department_id").innerHTML = this.$t(
          "DepartmentError"
        );
      }
      this.$validator.validateAll().then((result) => {
        if (
          this.staffs.department_id == undefined ||
          this.staffs.department_id == null ||
          this.staffs.department_id == ""
        ) {
          return false;
        }
        if (result) {
          this.addstaffs();
        }
      });
    },

    /**
     *  updateForm method for updating values of form
     **/
    updateForm() {
      this.isSubmit = true;
      if (
        this.staffs.department_id == undefined ||
        this.staffs.department_id == null ||
        this.staffs.department_id == ""
      ) {
        document.getElementById("department_id").innerHTML = this.$t(
          "DepartmentError"
        );
      }
      this.$validator.validateAll().then((result) => {
        if (
          this.staffs.department_id == undefined ||
          this.staffs.department_id == null ||
          this.staffs.department_id == ""
        ) {
          return false;
        }
        if (result) {
          this.updatestaffs();
        }
      });
    },

    /**
     *  addstaffs method for add record in a staff list
     **/
    addstaffs() {
      if(this.selected != 'assistant' || (this.selected == 'assistant' && this.checkboxValues.length > 0)){
      this.show = true;
      this.staffs.user_role = this.selected;
      this.staffs.company_id = this.$store.getters.roles == 'assistant' ? this.$store.getters.currentUser.company_id :this.$store.getters.currentUser.id;
      this.staffs.is_device = this.staffs.is_device == true ? 1 : 0;
      this.staffs.is_unio = this.$store.getters.currentUser.is_unio;
      this.staffs.is_assigned = this.selected == 'assistant' ? this.checkboxValues : [];

      this.$store
        .dispatch("AddStaff", this.staffs)
        .then((res) => {
          if (res.data.data) {
            this.$router.push("/company/staff").catch(() => {});
            this.$toasted.show(this.$t("StaffAdded"), {
              className: "top-alert",
              position: "top-center",
              type: "success",
              duration: 2000,
            });
             this.show = false;
          } else {
            this.showCredit = false;
            this.newPopup = true;
            this.newPopup1 = true;
            this.newPopup2 = false;
            this.newPopup3 = false;
            this.showCredit = false;
            this.show = false;
            return false;
          }
        })
        .catch((error) => {
          this.$log.error(
            "File : Add.vue | Function : addstaffs() | Error :",
            error
          );
          this.show = false;
          // Error response
          this.error =
            this.$t(error.replace(/[\s\/]/g, "")) !==
            error.replace(/[\s\/]/g, "")
              ? this.$t(error.replace(/[\s\/]/g, ""))
              : error;
          this.$toasted.show(this.error, {
            className: "top-alert",
            position: "top-center",
            type: "error",
            duration: 2000,
          });
          return false;
        });
      }
    },

    /**
     *  updatestaffs method for update record in a staffs list
     **/
    updatestaffs() {
      if(this.selected != 'assistant' || (this.selected == 'assistant' && this.checkboxValues.length > 0)){

      this.show = true;
      this.staffs.user_role = this.selected;
      this.staffs.id = this.$route.params.id;
      this.staffs.is_device = this.staffs.is_device == true ? 1 : 0;
      this.staffs.is_unio = this.$store.getters.currentUser.is_unio;
      this.staffs.currentUser = this.staffs.id == this.$store.getters.currentUser.staff_id ? true : false
      this.staffs.is_assigned = this.selected == 'assistant' ? this.checkboxValues : [];
      this.$store
        .dispatch("UpdateStaff", this.staffs)
        .then((res) => {
          this.show = false;
          this.$router.push("/company/staff").catch(() => {});
          this.$toasted.show(this.$t("StaffUpdated"), {
            className: "top-alert",
            position: "top-center",
            type: "success",
            duration: 2000,
          });
        })
        .catch((error) => {
          this.$log.error(
            "File : Add.vue | Function : updatestaffs() | Error :",
            error
          );
          this.show = false;
          // Error response
          this.error =
            this.$t(error.replace(/[\s\/]/g, "")) !==
            error.replace(/[\s\/]/g, "")
              ? this.$t(error.replace(/[\s\/]/g, ""))
              : error;
          this.$toasted.show(this.error, {
            className: "top-alert",
            position: "top-center",
            type: "error",
            duration: 2000,
          });
          return false;
        });
      }
    },

    /**
     *  getstaffs method get all companies from company list
     **/
    getstaffs(id) {
      this.show = true;
      this.$store
        .dispatch("ShowStaff", id)
        .then((res) => {
          this.show = false;
          this.staffs = res.data.data;
          this.staffs.is_device = this.staffs.is_device == 1 ? true : false;
          this.staffs.department_id = this.staffs.departments.map(
            (department) => {
              return department.id;
            }
          );
          this.selected = this.staffs.user_role;
          // this.staffs.department_id.forEach((element, index) => {
          //   if (this.values.includes(element) == false) {
          //     this.staffs.department_id.splice(index, 1);
          //   }
          // });
        })
        .catch((error) => {
          this.$log.error(
            "File : List.vue | Function : getstaffs() | Error :",
            error
          );
          this.show = false;
          if(error == "record_not_found"){
            this.recordNotFound = true
            return false;
          }
          // Error response
          this.error =
            this.$t(error.replace(/[\s\/]/g, "")) !==
            error.replace(/[\s\/]/g, "")
              ? this.$t(error.replace(/[\s\/]/g, ""))
              : error;
          this.$toasted.show(this.error, {
            className: "top-alert",
            position: "top-center",
            type: "error",
            duration: 2000,
          });
          return false;
        });
    },

    changePlan(){
      this.newPopup = true;
      this.newPopup2 = true;
      this.showCredit = false;
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    changeAssign(value, id){;
      this.isSubmit = true;
      var staff_index = this.checkboxValues.findIndex(x => x.staff_id === id);
      if (staff_index != -1)
      this.checkboxValues[staff_index].is_assign = value == 1 ? 1 : 0;
      else
      this.checkboxValues.push({staff_id: id, is_assign: value == 1 ? 1 : 0})
    },

    /**
     *
     * getStaffs   method  for list all staffs data
     **/
    getStaffList() {
      this.show = true;
      this.role = this.$store.getters.roles;
      let data = [];
      let id = this.$store.getters.currentUser.id;
      data = {
        company_id: this.role == "assistant" ? this.$store.getters.currentUser.company_id :this.$store.getters.currentUser.id,
        assisatant_id: this.$route.params.id ? this.$route.params.id : ""
      };
     
      let action = "GetOnlyStaff";
      this.$store
        .dispatch(action, data)
        .then((res) => {
          this.show = false;
          this.staffList = res.data.data;

          for (let i = 0; i < this.staffList.length; i++) {

          // this.checkboxValues
          if(this.staffList[i].is_assistant == 1)
            this.checkboxValues.push({staff_id: this.staffList[i].id, is_assign: 1})
          }

          this.totalRows = this.staffList.length;
        })
        .catch((error) => {
          this.$log.error(
            "File : List.vue | Function : getStaffList() | Error :",
            error
          );
          this.show = false;
          // Error response

          this.error =
            this.$t(error.replace(/[\s\/]/g, "")) !==
            error.replace(/[\s\/]/g, "")
              ? this.$t(error.replace(/[\s\/]/g, ""))
              : error;
          this.$toasted.show(this.error, {
            className: "top-alert",
            position: "top-center",
            type: "error",
            duration: 2000,
          });

          return false;
        });
    },
  },
};
</script>

<style>
.align-center {
  text-align: center;
}

.purchase {
  display: block !important;
}

#update {
  display: inline-flex;
}

#cancel {
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  margin-left: 20px;
  margin-top: 20px;
}

.contact_number {
  padding: 0.4rem !important;
}

.charge td {
  width: 100%;
}
</style>










